import { type DepositAccount } from '@fruitful/types';
import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    FullScreenSpinner,
    SafeAreaView,
    ScreenContainer,
    useToastController,
    View,
} from 'smoothie';

import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { formattedDollarsToCents } from '../../../../common/utils/bankingUtils';
import { invertNormalizedValue } from '../../../../common/utils/cardUtils';
import LogRocketFacade from '../../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../../logger/Logger';
import { useUser } from '../../../../user/providers/UserProvider';
import useCreateDepositoryTransfer from '../../../hooks/useCreateDepositoryTransfer';
import useGetAccountDisplayDetailsFromGlobalState from '../../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import useGetAllBankAccountDetails from '../../../hooks/useGetAllBankAccountDetails';
import useGetInternalAndExternalDepositAccounts from '../../../hooks/useGetInternalAndExternalDepositAccounts';
import { NeedsReviewSheet } from '../../transfers/NeedsReview';
import SelectAccountSheet from '../../transfers/SelectAccountSheet';
import {
    ExternalAccountPayment,
    MakePaymentTab,
    PaymentPostedTab,
    PaymentSelection,
    ReviewPaymentTab,
    SelectPaymentAmountSheet,
} from './MakePaymentTabs';

enum MakePaymentStep {
    MakePayment = 'makePayment',
    ExternalAccount = 'externalAccount',
    ConfirmPayment = 'confirmPayment',
    PaymentPosted = 'paymentPosted',
}

const SceneWrapper = ({ children }: { children: React.ReactNode }) => (
    <View
        $sm={{
            maxWidth: 700,
            alignSelf: 'center',
            width: '100%',
        }}
        height="100%"
    >
        {children}
    </View>
);

const MakePayment = () => {
    LogRocketFacade.tagPage('MakePayment');

    const [index, setIndex] = useState(0);
    const [tabHistoryStack, setTabHistoryStack] = useState<number[]>([0]);
    const [paymentSelection, setPaymentSelection] = useState<
        PaymentSelection | undefined
    >();
    const [externalAccountForPayment, setExternalAccountforPayment] = useState<
        DepositAccount | undefined
    >();
    const [isFromExternalAccountSheetOpen, setIsFromExternalAccountSheetOpen] =
        useState(false);
    const [isSelectPaymentAmountSheetOpen, setIsSelectPaymentAmountSheetOpen] =
        useState(false);
    const [paymentAmount, setPaymentAmount] = useState<number>();
    const user = useAuthedUser();
    const toast = useToastController();
    const routes = Object.values(MakePaymentStep).map((step) => ({
        key: step,
    }));

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showEmptyScreen, setShowEmptyScreen] = useState(false);
    const [isReviewSheetOpen, setIsReviewSheetOpen] = useState(false);

    const { refetch: refetchUser } = useUser();

    const {
        data: depositAccounts,
        isLoading: isLoadingDepositAccounts,
        refetch: refetchDepositAccounts,
    } = useGetInternalAndExternalDepositAccounts({});

    const onSuccessfulPlaidLink = () => {
        void refetchUser?.();
        void refetchDepositAccounts();
    };

    const getRouteIndex = (step: MakePaymentStep) =>
        routes.findIndex(({ key }) => key === step);

    const externalAccountIndex = getRouteIndex(MakePaymentStep.ExternalAccount);
    const reviewIndex = getRouteIndex(MakePaymentStep.ConfirmPayment);
    const paymentPostedIndex = getRouteIndex(MakePaymentStep.PaymentPosted);

    const { syncteraAccountId } = useLocalSearchParams<{
        syncteraAccountId: string;
    }>();

    const { mutateAsync: createDepositoryTransfer } =
        useCreateDepositoryTransfer();

    const { refetchAccountDetails, isLoading: isLoadingAccountDetails } =
        useGetAllBankAccountDetails(false);

    const handleSetNextPage = (nextPage: number) => {
        setIndex(nextPage);
        setTabHistoryStack((prevState) => {
            return [...prevState, nextPage];
        });
    };

    const goToNextPage = () => {
        handleSetNextPage(index + 1);
    };

    const onPressBack = () => {
        if (index === externalAccountIndex) {
            setExternalAccountforPayment(undefined);
        }

        if (tabHistoryStack.length > 1) {
            setIndex(tabHistoryStack[tabHistoryStack.length - 2] ?? 0);
            setTabHistoryStack((prevState) => {
                return prevState.filter((i) => i !== index);
            });
        } else {
            if (router.canGoBack()) {
                router.back();
            } else {
                router.replace('/home/banking');
            }
        }
    };

    const onPressClose = () => {
        // On payment posted screen, exit the entire flow
        if (index === paymentPostedIndex) {
            if (router.canGoBack()) {
                router.back();
            } else {
                router.replace('/home/banking');
            }
        } else {
            // On other screens, use the same logic as back button
            onPressBack();
        }
    };
    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    if (account === null || isSubmitting || isLoadingAccountDetails) {
        return <FullScreenSpinner />;
    }

    const {
        chargeSecuredSyncteraAccountId,
        balanceDue,
        autoPayEnabled,
        cardBalance,
    } = account;

    const cardBalanceNormalized = invertNormalizedValue(cardBalance);

    const preselectedPaymentOption =
        paymentSelection ??
        (account?.balanceDue != null && account.balanceDue !== '$0.00'
            ? PaymentSelection.PriorBalance
            : account?.cardBalance != null && account?.cardBalance !== '$0.00'
              ? PaymentSelection.OutstandingBalance
              : PaymentSelection.CustomAmount);

    const getPreselectedPaymentAmount = (paymentOption: PaymentSelection) => {
        switch (paymentOption) {
            case PaymentSelection.PriorBalance:
                return formattedDollarsToCents(account?.balanceDue ?? '0.00');
            case PaymentSelection.OutstandingBalance:
                return formattedDollarsToCents(cardBalanceNormalized);
            case PaymentSelection.CustomAmount:
                return paymentAmount ?? 0;
        }
    };

    const confirmPayment = async () => {
        if (
            chargeSecuredSyncteraAccountId == null ||
            syncteraAccountId == null
        ) {
            return;
        }

        try {
            setIsSubmitting(true);
            await createDepositoryTransfer({
                body: {
                    amount: paymentAmount as number,
                    syncteraPersonId: user.syncteraPersonId,
                    fromDepositAccount: {
                        id: externalAccountForPayment?.id ?? syncteraAccountId,
                        isExternal: externalAccountForPayment != null,
                    },
                    toDepositAccount: {
                        id: chargeSecuredSyncteraAccountId,
                        isExternal: false,
                        isChargeSecuredAccount: true,
                    },
                },
            });

            void refetchAccountDetails();
            goToNextPage();
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: MakePaymentStep } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        switch (route.key) {
            case MakePaymentStep.MakePayment:
                return (
                    <SceneWrapper>
                        <MakePaymentTab
                            balanceDue={balanceDue ?? '$0.00'}
                            selectedPaymentOption={preselectedPaymentOption}
                            cardBalance={cardBalanceNormalized}
                            onPress={(selection, amount) => {
                                setPaymentSelection(selection);
                                setPaymentAmount(amount);
                                handleSetNextPage(reviewIndex);
                            }}
                            onPressUseExternalAccount={(selection, amount) => {
                                setPaymentSelection(selection);
                                setPaymentAmount(amount);
                                handleSetNextPage(externalAccountIndex);
                            }}
                        />
                    </SceneWrapper>
                );
            case MakePaymentStep.ExternalAccount:
                return (
                    <SceneWrapper>
                        <ExternalAccountPayment
                            account={account}
                            externalPaymentAccount={externalAccountForPayment}
                            paymentAmount={
                                paymentAmount ??
                                getPreselectedPaymentAmount(
                                    preselectedPaymentOption,
                                )
                            }
                            onPressReview={() => {
                                setPaymentAmount(
                                    paymentAmount ??
                                        getPreselectedPaymentAmount(
                                            preselectedPaymentOption,
                                        ),
                                ); // Set the payment amount if the default is not modified
                                handleSetNextPage(reviewIndex);
                            }}
                            onPressSelectAccount={() => {
                                setIsFromExternalAccountSheetOpen(true);
                            }}
                            onPressSelectAmount={() => {
                                setIsSelectPaymentAmountSheetOpen(true);
                            }}
                        />
                    </SceneWrapper>
                );
            case MakePaymentStep.ConfirmPayment:
                return (
                    <SceneWrapper>
                        <ReviewPaymentTab
                            account={account}
                            externalPaymentAccount={externalAccountForPayment}
                            paymentAmount={paymentAmount as number}
                            onPress={confirmPayment}
                        />
                    </SceneWrapper>
                );
            case MakePaymentStep.PaymentPosted:
                return (
                    <SceneWrapper>
                        <PaymentPostedTab
                            onPressSetUpAutoPay={() => {
                                router.navigate({
                                    pathname: '/banking/card/enable-autopay',
                                    params: { syncteraAccountId },
                                });
                            }}
                            autoPayEnabled={autoPayEnabled}
                            onPressDone={router.back}
                        />
                    </SceneWrapper>
                );
            default:
                return <></>;
        }
    };

    if (isLoadingDepositAccounts) {
        return <FullScreenSpinner />;
    }

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );

    if (showEmptyScreen) {
        return (
            <SafeAreaView
                style={{ height: '100%', backgroundColor: 'white' }}
            ></SafeAreaView>
        );
    }

    // Show back button when not on first tab, close button when on first tab
    // On payment posted screen: show close button and no title
    const isOnFirstTab = index === 0;
    const isOnPaymentPostedScreen = index === paymentPostedIndex;

    return (
        <ScreenContainer
            title={
                isOnPaymentPostedScreen
                    ? undefined
                    : reviewIndex === index
                      ? 'Review payment'
                      : 'Make a payment'
            }
            hasBackButton={!isOnFirstTab && !isOnPaymentPostedScreen}
            hasCloseButton={isOnFirstTab || isOnPaymentPostedScreen}
            onBack={onPressBack}
            onClose={onPressClose}
        >
            <View height="100%">
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    renderTabBar={() => <></>}
                    onIndexChange={setIndex}
                />
            </View>
            {isFromExternalAccountSheetOpen && (
                <SelectAccountSheet
                    isOpen={isFromExternalAccountSheetOpen}
                    depositAccounts={externalAccounts}
                    onClose={() => {
                        setIsFromExternalAccountSheetOpen(false);
                    }}
                    onPlaidExit={() => {
                        setShowEmptyScreen(false);
                    }}
                    onSelectAccount={(account) => {
                        setExternalAccountforPayment(account);
                    }}
                    enableLinkingNewAccount={
                        user.externalAccountLinkingEnabled ?? false
                    }
                    onSuccessfulPlaidLink={onSuccessfulPlaidLink}
                    onNeedsReview={() => {
                        setIsReviewSheetOpen(false);
                    }}
                />
            )}
            {isSelectPaymentAmountSheetOpen && (
                <SelectPaymentAmountSheet
                    isAmountSheetOpen={isSelectPaymentAmountSheetOpen}
                    onClose={() => {
                        setIsSelectPaymentAmountSheetOpen(false);
                    }}
                    balanceDue={balanceDue ?? '$0.00'}
                    selectedPaymentOption={preselectedPaymentOption}
                    cardBalance={cardBalanceNormalized}
                    onPress={(selection, amount) => {
                        setPaymentSelection(selection);
                        setPaymentAmount(amount);
                        setIsSelectPaymentAmountSheetOpen(false);
                    }}
                />
            )}
            <NeedsReviewSheet
                isOpen={isReviewSheetOpen}
                onClose={() => {
                    setIsReviewSheetOpen(false);
                }}
            />
        </ScreenContainer>
    );
};

export default MakePayment;
